
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import "./FinancialPage.css";

export default function FinancialPage() {
  const { progressId, userId } = useParams();

   

  const [spendingTracked, setSpendingTracked] =
    useState(false);

  const [budgetFollowed, setBudgetFollowed] =
    useState(false);

  const [savingsAmount, setSavingsAmount] =
    useState("");

  const [financialLearningMinutes, setFinancialLearningMinutes] =
    useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateFinancial = useMutation(
    api.progress.calculateFinancial
  );

  /*
   * =========================================
   * QUERY STORED FINANCIAL RESULTS
   * =========================================
   */

  const usualresult = useQuery(
    api.progress.getFinancialResults,
    progressId && userId
      ? {
          userId,
          progressId,
        }
      : "skip"
  );


  const dailyRecords =
    usualresult?.dailyRecords ?? [];

  const weeklyRecords =
    usualresult?.weeklyRecords ?? [];

  /*
   * =========================================
   * SORT DAILY RECORDS
   * =========================================
   */

  const sortedDailyRecords = [
    ...dailyRecords,
  ].sort((a, b) =>
    a.date.localeCompare(b.date)
  );

  /*
   * =========================================
   * SORT WEEKLY RECORDS
   * =========================================
   */

  const sortedWeeklyRecords = [
    ...weeklyRecords,
  ].sort((a, b) =>
    a.weekStart.localeCompare(b.weekStart)
  );

  /*
   * =========================================
   * LATEST WEEKLY RECORD
   * =========================================
   */

  const latestWeeklyRecord =
    sortedWeeklyRecords.length > 0
      ? sortedWeeklyRecords[
          sortedWeeklyRecords.length - 1
        ]
      : null;

  /*
   * =========================================
   * DAILY SCORE CHART
   * =========================================
   */

  const dailyScoreChartData =
    sortedDailyRecords.map((record) => ({
      date: record.date,

      dailyScore: Math.round(
        record.dailyScore ?? 0
      ),
    }));

  /*
   * =========================================
   * WEEKLY SCORE CHART
   * =========================================
   */

  const weeklyScoreChartData =
    sortedWeeklyRecords.map((record) => ({
      week: record.weekStart,

      trackingScore: Math.round(
        record.trackingScore ?? 0
      ),

      activityScore: Math.round(
        record.activityScore ?? 0
      ),

      finalScore: Math.round(
        record.finalScore ?? 0
      ),
    }));

  /*
   * =========================================
   * FINANCIAL DATA CHART
   * =========================================
   */

  const financialData =
    latestWeeklyRecord
      ? [
          {
            name: "Savings",
            value:
              latestWeeklyRecord.totalSavingsAmount ??
              0,
          },
          {
            name: "Learning",
            value:
              latestWeeklyRecord.averageFinancialLearningMinutes ??
              0,
          },
        ]
      : [];

  /*
   * =========================================
   * GET TODAY
   * =========================================
   */

  const getToday = () => {
    return new Date()
      .toISOString()
      .split("T")[0];
  };

  /*
   * =========================================
   * GET CURRENT WEEK
   * =========================================
   */

  const getCurrentWeek = () => {
    const today = new Date();

    const day = today.getDay();

    const daysSinceMonday =
      day === 0 ? 6 : day - 1;

    const monday = new Date(today);

    monday.setDate(
      today.getDate() -
        daysSinceMonday
    );

    const sunday = new Date(monday);

    sunday.setDate(
      monday.getDate() + 6
    );

    const weekStart =
      monday.toISOString().split("T")[0];

    const weekEnd =
      sunday.toISOString().split("T")[0];

    return {
      weekStart,
      weekEnd,
    };
  };

  /*
   * =========================================
   * SAVE TODAY + CALCULATE WEEK
   * =========================================
   */

  const handleCalculate = async (e) => {
    e.preventDefault();

    setError("");
    setResult(null);

    /*
     * Validate IDs
     */

    if (!progressId || !userId) {
      setError(
        "Missing progress or user information."
      );
      return;
    }

    /*
     * Validate savings
     */

    if (savingsAmount === "") {
      setError(
        "Please enter your savings amount."
      );
      return;
    }

    /*
     * Validate learning minutes
     */

    if (
      financialLearningMinutes === ""
    ) {
      setError(
        "Please enter your financial learning minutes."
      );
      return;
    }

    const savings =
      Number(savingsAmount);

    const learningMinutes =
      Number(financialLearningMinutes);

    /*
     * Validate numbers
     */

    if (
      Number.isNaN(savings) ||
      Number.isNaN(learningMinutes)
    ) {
      setError(
        "Please enter valid numbers."
      );
      return;
    }

    /*
     * Prevent negative values
     */

    if (savings < 0) {
      setError(
        "Savings amount cannot be negative."
      );
      return;
    }

    if (learningMinutes < 0) {
      setError(
        "Learning minutes cannot be negative."
      );
      return;
    }

    try {
      setLoading(true);

      /*
       * Today's date
       */

      const date = getToday();

      /*
       * Current week
       */

      const {
        weekStart,
        weekEnd,
      } = getCurrentWeek();

      /*
       * =========================================
       * SAVE DATA
       * =========================================
       */

      const response =
        await calculateFinancial({
          userId,
          progressId,

          date,

          spendingTracked,
          budgetFollowed,

          savingsAmount: savings,

          financialLearningMinutes:
            learningMinutes,

          weekStart,
          weekEnd,
        });

      

      /*
       * Keep mutation result only for the
       * immediate daily result.
       *
       * Charts use the Convex query instead.
       */

      setResult({
        ...response,
        date,
      });

    } catch (err) {
      console.error(
        "Financial Error:",
        err
      );

      setError(
        err?.message ||
          "Failed to calculate Financial score."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="financial-page">
      <div className="financial-container">

        {/* =================================
            HEADER
        ================================= */}

        <div className="financial-header">
          <div className="financial-eyebrow">
            MONEY & PERSONAL FINANCE
          </div>

          <h1 className="financial-title">
            Financial
          </h1>

          <p className="financial-subtitle">
            Track your financial habits,
            savings, and financial learning.
          </p>
        </div>

        {/* =================================
            DAILY TRACKING FORM
        ================================= */}

        <div className="financial-form-card">

          <div className="financial-form-header">
            <span className="financial-card-eyebrow">
              DAILY TRACKING
            </span>

            <h2>
              Today's Financial Tracking
            </h2>

            <p>
              Keep track of your daily money
              habits and learning activity.
            </p>
          </div>

          {/* Spending Tracked */}

          <div className="financial-checkbox-group">

            <label className="financial-checkbox-label">

              <input
                type="checkbox"
                checked={spendingTracked}
                onChange={(e) =>
                  setSpendingTracked(
                    e.target.checked
                  )
                }
              />

              <span className="financial-custom-checkbox"></span>

              <span>
                I tracked my spending today
              </span>

            </label>

          </div>

          {/* Budget Followed */}

          <div className="financial-checkbox-group">

            <label className="financial-checkbox-label">

              <input
                type="checkbox"
                checked={budgetFollowed}
                onChange={(e) =>
                  setBudgetFollowed(
                    e.target.checked
                  )
                }
              />

              <span className="financial-custom-checkbox"></span>

              <span>
                I followed my budget today
              </span>

            </label>

          </div>

          {/* Savings */}

          <div className="financial-input-group">

            <label>
              Savings Amount
            </label>

            <input
              type="number"
              min="0"
              step="0.01"
              value={savingsAmount}
              onChange={(e) =>
                setSavingsAmount(
                  e.target.value
                )
              }
              placeholder="Example: 25.00"
              className="financial-input"
            />

          </div>

          {/* Financial Learning */}

          <div className="financial-input-group">

            <label>
              Financial Learning Minutes
            </label>

            <input
              type="number"
              min="0"
              value={
                financialLearningMinutes
              }
              onChange={(e) =>
                setFinancialLearningMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 30"
              className="financial-input"
            />

          </div>

          {/* Error */}

          {error && (
            <div className="financial-error">
              {error}
            </div>
          )}

          {/* Submit */}

          <button
            onClick={handleCalculate}
            disabled={loading}
            className="financial-submit"
          >
            {loading
              ? "Saving..."
              : "Save Today's Financial Tracking"}
          </button>

        </div>

        {/* =================================
            TODAY'S RESULT
        ================================= */}

        {result && (
          <div className="financial-result-section">

            <div className="financial-card financial-today-result">

              <div>
                <span className="financial-card-eyebrow">
                  TODAY
                </span>

                <p className="financial-score-label">
                  Today's Financial Score
                </p>

                <p className="financial-result-date">
                  {result.date}
                </p>
              </div>

              <p className="financial-score-value">
                {Math.round(
                  result.dailyScore ?? 0
                )}

                <span>
                  /100
                </span>
              </p>

            </div>

          </div>
        )}

        {/* =================================
            STORED RESULTS
        ================================= */}

        {usualresult && (
          <div className="financial-results">

            {/* =================================
                LATEST WEEKLY SCORE CARDS
            ================================= */}

            {latestWeeklyRecord && (
              <div className="financial-weekly-section">

                <div className="financial-section-heading">
                  <span className="financial-card-eyebrow">
                    WEEKLY OVERVIEW
                  </span>

                  <h2 className="financial-section-title">
                    Weekly Financial Score
                  </h2>
                </div>

                <div className="financial-score-grid">

                  {/* Tracking */}

                  <div className="financial-score-card tracking-card">

                    <p>
                      Tracking Score
                    </p>

                    <p className="financial-small-score">
                      {Math.round(
                        latestWeeklyRecord.trackingScore ??
                          0
                      )}

                      <span>
                        /100
                      </span>
                    </p>

                  </div>

                  {/* Activity */}

                  <div className="financial-score-card activity-card">

                    <p>
                      Activity Score
                    </p>

                    <p className="financial-small-score">
                      {Math.round(
                        latestWeeklyRecord.activityScore ??
                          0
                      )}

                      <span>
                        /100
                      </span>
                    </p>

                  </div>

                  {/* Final */}

                  <div className="financial-score-card final-card">

                    <p>
                      Final Score
                    </p>

                    <p className="financial-small-score">
                      {Math.round(
                        latestWeeklyRecord.finalScore ??
                          0
                      )}

                      <span>
                        /100
                      </span>
                    </p>

                  </div>

                </div>

              </div>
            )}

            {/* =================================
                DAILY SCORE CHART
            ================================= */}

            {dailyScoreChartData.length >= 0 && (
              <div className="financial-card financial-chart-card">

                <h2 className="financial-section-title">
                  Daily Financial Score
                </h2>

                <div className="financial-chart-container financial-chart-medium">

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >

                    <BarChart
                      data={
                        dailyScoreChartData
                      }
                    >

                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#26372d"
                      />

                      <XAxis
                        dataKey="date"
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={{
                          stroke: "#33443a",
                        }}
                      />

                      <YAxis
                        domain={[0, 100]}
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={{
                          stroke: "#33443a",
                        }}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0b120e",
                          border: "1px solid #2f513c",
                          borderRadius: "12px",
                          color: "#f8fafc",
                        }}
                      />

                      <Legend
                        wrapperStyle={{
                          color: "#94a3b8",
                        }}
                      />

                      <Bar
                        dataKey="dailyScore"
                        name="Daily Score"
                        fill="#22c55e"
                        radius={[
                          7,
                          7,
                          0,
                          0,
                        ]}
                      />

                    </BarChart>

                  </ResponsiveContainer>

                </div>

              </div>
            )}

            {/* =================================
                DUPLICATE DAILY SCORE CHART
            ================================= */}

            <div className="financial-card financial-chart-card">

              <h2 className="financial-section-title">
                Daily Financial Score
              </h2>

              <div className="financial-chart-container financial-chart-small">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <BarChart
                    data={
                      dailyScoreChartData
                    }
                  >

                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#26372d"
                    />

                    <XAxis
                      dataKey="date"
                      tick={{
                        fill: "#94a3b8",
                        fontSize: 12,
                      }}
                      axisLine={{
                        stroke: "#33443a",
                      }}
                      tickLine={{
                        stroke: "#33443a",
                      }}
                    />

                    <YAxis
                      domain={[0, 100]}
                      tick={{
                        fill: "#94a3b8",
                        fontSize: 12,
                      }}
                      axisLine={{
                        stroke: "#33443a",
                      }}
                      tickLine={{
                        stroke: "#33443a",
                      }}
                    />

                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0b120e",
                        border: "1px solid #2f513c",
                        borderRadius: "12px",
                        color: "#f8fafc",
                      }}
                    />

                    <Legend
                      wrapperStyle={{
                        color: "#94a3b8",
                      }}
                    />

                    <Bar
                      dataKey="dailyScore"
                      name="Daily Score"
                      fill="#22c55e"
                      radius={[
                        7,
                        7,
                        0,
                        0,
                      ]}
                    />

                  </BarChart>

                </ResponsiveContainer>

              </div>

            </div>

            {/* =================================
                WEEKLY SCORE CHART
            ================================= */}

            {weeklyScoreChartData.length > 0 && (
              <div className="financial-card financial-chart-card">

                <h2 className="financial-section-title">
                  Weekly Financial Scores
                </h2>

                <div className="financial-chart-container financial-chart-medium">

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >

                    <BarChart
                      data={
                        weeklyScoreChartData
                      }
                    >

                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#26372d"
                      />

                      <XAxis
                        dataKey="week"
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={{
                          stroke: "#33443a",
                        }}
                      />

                      <YAxis
                        domain={[0, 100]}
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={{
                          stroke: "#33443a",
                        }}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0b120e",
                          border: "1px solid #2f513c",
                          borderRadius: "12px",
                          color: "#f8fafc",
                        }}
                      />

                      <Legend
                        wrapperStyle={{
                          color: "#94a3b8",
                        }}
                      />

                      <Bar
                        dataKey="trackingScore"
                        name="Tracking Score"
                        fill="#22c55e"
                        radius={[
                          7,
                          7,
                          0,
                          0,
                        ]}
                      />

                      <Bar
                        dataKey="activityScore"
                        name="Activity Score"
                        fill="#84cc16"
                        radius={[
                          7,
                          7,
                          0,
                          0,
                        ]}
                      />

                      <Bar
                        dataKey="finalScore"
                        name="Final Score"
                        fill="#f59e0b"
                        radius={[
                          7,
                          7,
                          0,
                          0,
                        ]}
                      />

                    </BarChart>

                  </ResponsiveContainer>

                </div>

              </div>
            )}

            {/* =================================
                FINANCIAL OVERVIEW / WEEKLY SCORES
            ================================= */}

            <div className="financial-card financial-chart-card">

              <h2 className="financial-section-title">
                Weekly Financial Scores
              </h2>

              <p className="financial-muted-text">
                Weekly records: {weeklyRecords.length}
              </p>

              <div className="financial-chart-container financial-chart-small">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <BarChart
                    data={
                      weeklyScoreChartData
                    }
                  >

                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#26372d"
                    />

                    <XAxis
                      dataKey="week"
                      tick={{
                        fill: "#94a3b8",
                        fontSize: 12,
                      }}
                      axisLine={{
                        stroke: "#33443a",
                      }}
                      tickLine={{
                        stroke: "#33443a",
                      }}
                    />

                    <YAxis
                      domain={[0, 100]}
                      tick={{
                        fill: "#94a3b8",
                        fontSize: 12,
                      }}
                      axisLine={{
                        stroke: "#33443a",
                      }}
                      tickLine={{
                        stroke: "#33443a",
                      }}
                    />

                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0b120e",
                        border: "1px solid #2f513c",
                        borderRadius: "12px",
                        color: "#f8fafc",
                      }}
                    />

                    <Legend
                      wrapperStyle={{
                        color: "#94a3b8",
                      }}
                    />

                    <Bar
                      dataKey="trackingScore"
                      name="Tracking Score"
                      fill="#22c55e"
                      radius={[
                        7,
                        7,
                        0,
                        0,
                      ]}
                    />

                    <Bar
                      dataKey="activityScore"
                      name="Activity Score"
                      fill="#84cc16"
                      radius={[
                        7,
                        7,
                        0,
                        0,
                      ]}
                    />

                    <Bar
                      dataKey="finalScore"
                      name="Final Score"
                      fill="#f59e0b"
                      radius={[
                        7,
                        7,
                        0,
                        0,
                      ]}
                    />

                  </BarChart>

                </ResponsiveContainer>

              </div>

            </div>

            {/* =================================
                FINANCIAL OVERVIEW
            ================================= */}

            {financialData.length > 0 && (
              <div className="financial-card financial-chart-card">

                <h2 className="financial-section-title">
                  Financial Overview
                </h2>

                <p className="financial-muted-text">
                  Based on the latest stored
                  weekly Financial record.
                </p>

                <div className="financial-chart-container financial-chart-medium">

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >

                    <BarChart
                      data={financialData}
                    >

                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#26372d"
                      />

                      <XAxis
                        dataKey="name"
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={{
                          stroke: "#33443a",
                        }}
                      />

                      <YAxis
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={{
                          stroke: "#33443a",
                        }}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0b120e",
                          border: "1px solid #2f513c",
                          borderRadius: "12px",
                          color: "#f8fafc",
                        }}
                      />

                      <Legend
                        wrapperStyle={{
                          color: "#94a3b8",
                        }}
                      />

                      <Bar
                        dataKey="value"
                        name="Value"
                        fill="#22c55e"
                        radius={[
                          7,
                          7,
                          0,
                          0,
                        ]}
                      />

                    </BarChart>

                  </ResponsiveContainer>

                </div>

              </div>
            )}

            {/* =================================
                WEEKLY DETAILS
            ================================= */}

            {latestWeeklyRecord && (
              <div className="financial-card financial-details-card">

                <h2 className="financial-section-title">
                  Weekly Details
                </h2>

                <div className="financial-details-grid">

                  <p className="financial-detail-item">
                    Week:{" "}
                    <strong>
                      {latestWeeklyRecord.weekStart}
                      {" "}
                      -
                      {" "}
                      {latestWeeklyRecord.weekEnd}
                    </strong>
                  </p>

                  <p className="financial-detail-item">
                    Days measured:{" "}
                    <strong>
                      {latestWeeklyRecord.daysMeasured}
                    </strong>
                  </p>

                  <p className="financial-detail-item">
                    Financial suggestions:{" "}
                    <strong>
                      {latestWeeklyRecord.suggestionCount}
                    </strong>
                  </p>

                  <p className="financial-detail-item">
                    Completed activities:{" "}
                    <strong>
                      {latestWeeklyRecord.activityCount ??
                        0}
                    </strong>
                  </p>

                  <p className="financial-detail-item">
                    Total savings:{" "}
                    <strong>
                      $
                      {Number(
                        latestWeeklyRecord.totalSavingsAmount ??
                          0
                      ).toFixed(2)}
                    </strong>
                  </p>

                  <p className="financial-detail-item">
                    Average financial learning:{" "}
                    <strong>
                      {Number(
                        latestWeeklyRecord.averageFinancialLearningMinutes ??
                          0
                      ).toFixed(1)}
                      {" "}
                      min/day
                    </strong>
                  </p>

                </div>

              </div>
            )}

            {/* =================================
                NO RESULTS
            ================================= */}

            {dailyRecords.length === 0 &&
              weeklyRecords.length === 0 && (
                <div className="financial-card financial-no-results">
                  No Financial results have been
                  recorded yet.
                </div>
              )}

          </div>
        )}

      </div>
    </div>
  );
}
